import { DownloadOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import styled from 'styled-components';

import { hostingStoragePathToUrl } from 'services/Firebase';
import { type VideoBundleElement } from 'stores/VideoBundleElement';
import { last } from 'utils/Array';
import { offerDownload } from 'utils/Download';

const Container = styled.div`
  align-items: center;
  border-bottom: 1px solid #e5e5e5;
  display: grid;
  grid-template-columns: 1fr auto auto;
  padding: 10px 0;
  width: 100%;
`;

const MetadataContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FileLabelContainer = styled.div`
  display: flex;
  gap: 12px;
`;

const PrincipalLabel = styled.div`
  font-weight: bold;
`;

const STATUS_PROPS_DEFAULT = { label: 'In progress', color: '#666' } as const;
const STATUS_PROPS: Record<string, { label: string; color: string }> = {
  complete: { label: 'Complete', color: '#33800d' },
  error: { label: 'Error', color: '#f5222d' },
};

const TaskStatus = styled.span<{ $backgroundColor: string }>`
  color: white;
  margin-left: 10px;
  background: ${(p) => p.$backgroundColor};
  padding: 2px 7px 4px;
  border-radius: 20px;
  font-size: 10px;
  position: relative;
  top: -2px;
`;

const ErrorText = styled.div`
  color: red;
  font-size: 12px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 7px;
  align-items: center;
`;

function formatDurationMsec(durationMs: number): string {
  return `${(durationMs / 1000).toFixed(1)}s`;
}

export interface VideoBundleElementRowProps {
  element: VideoBundleElement;
}

function VideoBundleElementRow({ element }: VideoBundleElementRowProps) {
  const elementData = element.data;

  const [width, height] = elementData.dimensions;

  const error = last(elementData.errors);

  const videoUrl = elementData.videoStorage
    ? hostingStoragePathToUrl(elementData.videoStorage)
    : '';

  const statusProps = STATUS_PROPS[elementData.status] || STATUS_PROPS_DEFAULT;

  const downloadVideo = async () => {
    if (videoUrl) {
      try {
        // TODO: i think the filename, by this point, has already gone
        //  through more rigorous sanitizing and dedupe-logic, so it
        //  may be sufficient to just chop the url at the last / and use
        //  that
        await offerDownload(videoUrl, `${elementData.label}.mp4`);
      } catch (error) {
        console.error('Error downloading video:', error);
        message.error('Failed to download video');
      }
    }
  };

  return (
    <Container>
      <MetadataContainer>
        <FileLabelContainer>
          <PrincipalLabel>
            {elementData.label}
            <TaskStatus $backgroundColor={statusProps.color}>
              {statusProps.label}
            </TaskStatus>
          </PrincipalLabel>
        </FileLabelContainer>

        <div>
          {`${width}x${height} @ ${elementData.frameRate}fps - ${formatDurationMsec(elementData.duration)}`}
        </div>

        {error && <ErrorText>•&nbsp;{error}</ErrorText>}
      </MetadataContainer>

      {videoUrl && (
        <ButtonsContainer>
          <Button
            type="primary"
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Video
          </Button>
          <Button onClick={downloadVideo} icon={<DownloadOutlined />} />
        </ButtonsContainer>
      )}
    </Container>
  );
}

export default observer(VideoBundleElementRow);
